/**
 * Werkorders Service - API calls voor werkorders
 */

import apiFetch from './api';

export type WerkorderStatus =
  | 'NIEUW'
  | 'GEPLAND'
  | 'IN_UITVOERING'
  | 'OPGESCHORT'
  | 'VOLTOOID'
  | 'GEANNULEERD';

export type WerkorderPrioriteit = 'LAAG' | 'NORMAAL' | 'HOOG' | 'SPOED';

export interface WerkorderRegel {
  id?: number;
  werkorder_id?: number;
  volgnummer: number;
  type_regel: string;
  voorraad_item_id?: number;
  omschrijving: string;
  aantal_gepland: number;
  aantal_werkelijk?: number;
  eenheid?: string;
  prijs_per_eenheid: number;
  korting_percentage?: number;
  korting_bedrag?: number;
  totaal: number;
  voltooid: boolean;
  voltooid_op?: string;
  actief: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface WerkorderTijdregistratie {
  id?: number;
  werkorder_id?: number;
  werknemer_id: number;
  datum: string;
  starttijd: string;
  eindtijd?: string;
  aantal_uren: number;
  omschrijving?: string;
  uurloon: number;
  totaal_kosten: number;
  goedgekeurd: boolean;
  goedgekeurd_door_werknemer_id?: number;
  goedgekeurd_op?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WerkorderMateriaalverbruik {
  id?: number;
  werkorder_id?: number;
  voorraad_item_id: number;
  datum: string;
  aantal: number;
  eenheid?: string;
  prijs_per_eenheid: number;
  totaal_kosten: number;
  geregistreerd_door_werknemer_id?: number;
  omschrijving?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Werkorder {
  id: number;
  werkordernummer: string;
  relatie_id: number;
  boot_id?: number;
  offerte_id?: number;
  status: WerkorderStatus;
  prioriteit: WerkorderPrioriteit;

  werkorder_datum: string;
  geplande_startdatum?: string;
  geplande_einddatum?: string;
  werkelijke_startdatum?: string;
  werkelijke_einddatum?: string;

  toegewezen_aan_werknemer_id?: number;

  onderwerp: string;
  omschrijving?: string;
  werkzaamheden?: string;
  interne_notities?: string;

  subtotaal: number;
  btw_percentage: number;
  btw_bedrag: number;
  totaal: number;
  korting_percentage?: number;
  korting_bedrag?: number;

  geschatte_uren?: number;
  werkelijke_uren?: number;
  geschatte_kosten?: number;
  werkelijke_kosten?: number;

  uurloon?: number;

  aangemaakt_door_user_id?: number;
  aangemaakt_door_werknemer_id?: number;
  afgerond_op?: string;
  afgerond_door_werknemer_id?: number;

  factuur_id?: number;
  gefactureerd_op?: string;

  actief: boolean;
  created_at: string;
  updated_at?: string;

  regels: WerkorderRegel[];
  tijdregistraties: WerkorderTijdregistratie[];
  materiaalverbruik: WerkorderMateriaalverbruik[];
}

export interface WerkorderRelatie {
  id: number;
  bedrijfsnaam: string;
  voornaam?: string;
  tussenvoegsel?: string;
  achternaam?: string;
}

export interface WerkorderList {
  id: number;
  werkordernummer: string;
  relatie_id: number;
  boot_id?: number;
  status: WerkorderStatus;
  prioriteit: WerkorderPrioriteit;
  werkorder_datum: string;
  geplande_startdatum?: string;
  toegewezen_aan_werknemer_id?: number;
  onderwerp: string;
  totaal: number;
  offerte_id?: number;
  created_at: string;
  relatie?: WerkorderRelatie;
}

export interface WerkorderCreate {
  relatie_id: number;
  boot_id?: number;
  offerte_id?: number;
  prioriteit?: WerkorderPrioriteit;

  werkorder_datum: string;
  geplande_startdatum?: string;
  geplande_einddatum?: string;

  toegewezen_aan_werknemer_id?: number;

  onderwerp: string;
  omschrijving?: string;
  werkzaamheden?: string;
  interne_notities?: string;

  subtotaal?: number;
  btw_percentage?: number;
  btw_bedrag?: number;
  totaal?: number;
  korting_percentage?: number;
  korting_bedrag?: number;

  geschatte_uren?: number;
  geschatte_kosten?: number;
  
  uurloon?: number;

  actief?: boolean;

  regels?: WerkorderRegel[];
}

export interface WerkorderUpdate {
  relatie_id?: number;
  boot_id?: number;
  status?: WerkorderStatus;
  prioriteit?: WerkorderPrioriteit;

  werkorder_datum?: string;
  geplande_startdatum?: string;
  geplande_einddatum?: string;
  werkelijke_startdatum?: string;
  werkelijke_einddatum?: string;

  toegewezen_aan_werknemer_id?: number;

  onderwerp?: string;
  omschrijving?: string;
  werkzaamheden?: string;
  interne_notities?: string;

  subtotaal?: number;
  btw_percentage?: number;
  btw_bedrag?: number;
  totaal?: number;
  korting_percentage?: number;
  korting_bedrag?: number;

  geschatte_uren?: number;
  werkelijke_uren?: number;
  geschatte_kosten?: number;
  werkelijke_kosten?: number;
  
  uurloon?: number;

  actief?: boolean;
}

export interface WerkorderFilters {
  skip?: number;
  limit?: number;
  status?: WerkorderStatus;
  prioriteit?: WerkorderPrioriteit;
  relatie_id?: number;
  boot_id?: number;
  toegewezen_aan_werknemer_id?: number;
  search?: string;
  van_datum?: string;
  tot_datum?: string;
}

// Urenregistratie types
export interface TijdregistratieOverzicht {
  id: number;
  werkorder_id: number;
  werkordernummer: string;
  werkorder_onderwerp: string;
  klant: string;
  werknemer_id: number;
  werknemer_naam: string;
  datum: string;
  starttijd?: string;
  eindtijd?: string;
  aantal_uren: number;
  omschrijving?: string;
  uurloon: number;
  totaal_kosten: number;
  goedgekeurd: boolean;
  goedgekeurd_op?: string;
  created_at: string;
}

export interface WeekDag {
  datum: string;
  dag_naam: string;
  registraties: {
    id: number;
    werkorder_id: number;
    werkordernummer: string;
    werkorder_onderwerp: string;
    klant: string;
    starttijd?: string;
    eindtijd?: string;
    aantal_uren: number;
    omschrijving?: string;
    uurloon: number;
    totaal_kosten: number;
    goedgekeurd: boolean;
  }[];
  totaal_uren: number;
}

export interface WeekOverzicht {
  werknemer: {
    id: number;
    naam: string;
    uurloon: number;
  };
  week_start: string;
  week_end: string;
  dagen: WeekDag[];
  totaal_uren: number;
  totaal_kosten: number;
}

export interface ActieveWerkorder {
  id: number;
  werkordernummer: string;
  onderwerp: string;
  klant: string;
  klant_id: number;
  status: string;
  offerte_id?: number;
}

export interface WerkorderTaak {
  id: number;
  omschrijving: string;
  aantal: number;
  eenheid: string;
}

export interface WerkorderSectie {
  id: number;
  naam: string;
  omschrijving?: string;
  taken: WerkorderTaak[];
}

export interface WerkorderTaken {
  werkorder_id: number;
  offerte_id?: number;
  secties: WerkorderSectie[];
}

export const werkordersService = {
  async getAll(filters: WerkorderFilters = {}): Promise<WerkorderList[]> {
    const params = new URLSearchParams();

    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.prioriteit) params.append('prioriteit', filters.prioriteit);
    if (filters.relatie_id !== undefined) params.append('relatie_id', filters.relatie_id.toString());
    if (filters.boot_id !== undefined) params.append('boot_id', filters.boot_id.toString());
    if (filters.toegewezen_aan_werknemer_id !== undefined) params.append('toegewezen_aan_werknemer_id', filters.toegewezen_aan_werknemer_id.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.van_datum) params.append('van_datum', filters.van_datum);
    if (filters.tot_datum) params.append('tot_datum', filters.tot_datum);

    return apiFetch(`/api/v1/werkorders?${params.toString()}`);
  },

  async getById(id: number): Promise<Werkorder> {
    return apiFetch(`/api/v1/werkorders/${id}`);
  },

  async create(data: WerkorderCreate): Promise<Werkorder> {
    return apiFetch('/api/v1/werkorders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(id: number, data: WerkorderUpdate): Promise<Werkorder> {
    return apiFetch(`/api/v1/werkorders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async updateStatus(id: number, status: WerkorderStatus): Promise<Werkorder> {
    return apiFetch(`/api/v1/werkorders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  async delete(id: number): Promise<void> {
    return apiFetch(`/api/v1/werkorders/${id}`, {
      method: 'DELETE',
    });
  },

  // Werkorder Regels
  async createRegel(werkorder_id: number, data: WerkorderRegel): Promise<WerkorderRegel> {
    return apiFetch(`/api/v1/werkorders/${werkorder_id}/regels`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRegel(regel_id: number, data: Partial<WerkorderRegel>): Promise<WerkorderRegel> {
    return apiFetch(`/api/v1/werkorders/regels/${regel_id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteRegel(regel_id: number): Promise<void> {
    return apiFetch(`/api/v1/werkorders/regels/${regel_id}`, {
      method: 'DELETE',
    });
  },

  // Tijdregistraties
  async getTijdregistraties(werkorder_id: number): Promise<WerkorderTijdregistratie[]> {
    return apiFetch(`/api/v1/werkorders/${werkorder_id}/tijdregistraties`);
  },

  async createTijdregistratie(werkorder_id: number, data: WerkorderTijdregistratie): Promise<WerkorderTijdregistratie> {
    return apiFetch(`/api/v1/werkorders/${werkorder_id}/tijdregistraties`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTijdregistratie(registratie_id: number, data: Partial<WerkorderTijdregistratie>): Promise<WerkorderTijdregistratie> {
    return apiFetch(`/api/v1/werkorders/tijdregistraties/${registratie_id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteTijdregistratie(registratie_id: number): Promise<void> {
    return apiFetch(`/api/v1/werkorders/tijdregistraties/${registratie_id}`, {
      method: 'DELETE',
    });
  },

  // Materiaalverbruik
  async getMateriaalverbruik(werkorder_id: number): Promise<WerkorderMateriaalverbruik[]> {
    return apiFetch(`/api/v1/werkorders/${werkorder_id}/materiaalverbruik`);
  },

  async createMateriaalverbruik(werkorder_id: number, data: WerkorderMateriaalverbruik): Promise<WerkorderMateriaalverbruik> {
    return apiFetch(`/api/v1/werkorders/${werkorder_id}/materiaalverbruik`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateMateriaalverbruik(verbruik_id: number, data: Partial<WerkorderMateriaalverbruik>): Promise<WerkorderMateriaalverbruik> {
    return apiFetch(`/api/v1/werkorders/materiaalverbruik/${verbruik_id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteMateriaalverbruik(verbruik_id: number): Promise<void> {
    return apiFetch(`/api/v1/werkorders/materiaalverbruik/${verbruik_id}`, {
      method: 'DELETE',
    });
  },

  // Urenregistratie Overzicht
  async getTijdregistratiesOverzicht(filters: {
    werknemer_id?: number;
    van_datum?: string;
    tot_datum?: string;
  } = {}): Promise<TijdregistratieOverzicht[]> {
    const params = new URLSearchParams();
    if (filters.werknemer_id) params.append('werknemer_id', filters.werknemer_id.toString());
    if (filters.van_datum) params.append('van_datum', filters.van_datum);
    if (filters.tot_datum) params.append('tot_datum', filters.tot_datum);
    return apiFetch(`/api/v1/werkorders/tijdregistraties/overzicht?${params.toString()}`);
  },

  async getWeekOverzicht(werknemer_id: number, week_start: string): Promise<WeekOverzicht> {
    return apiFetch(`/api/v1/werkorders/tijdregistraties/week/${werknemer_id}?week_start=${week_start}`);
  },

  async getActieveWerkorders(): Promise<ActieveWerkorder[]> {
    return apiFetch('/api/v1/werkorders/actief');
  },

  async getWerkorderTaken(werkorderId: number): Promise<WerkorderTaken> {
    return apiFetch(`/api/v1/werkorders/${werkorderId}/taken`);
  },

  // Helper functies
  berekenTotaal(regels: WerkorderRegel[]): number {
    return regels.reduce((sum, regel) => sum + regel.totaal, 0);
  },

  berekenBTW(subtotaal: number, btw_percentage: number): number {
    return (subtotaal * btw_percentage) / 100;
  },

  berekenRegelTotaal(regel: WerkorderRegel): number {
    const aantal = regel.aantal_werkelijk || regel.aantal_gepland;
    let totaal = aantal * regel.prijs_per_eenheid;

    if (regel.korting_percentage && regel.korting_percentage > 0) {
      const korting = (totaal * regel.korting_percentage) / 100;
      totaal -= korting;
    } else if (regel.korting_bedrag && regel.korting_bedrag > 0) {
      totaal -= regel.korting_bedrag;
    }

    return Math.max(0, totaal);
  },

  getStatusColor(status: WerkorderStatus): string {
    switch (status) {
      case 'NIEUW':
        return 'blue';
      case 'GEPLAND':
        return 'cyan';
      case 'IN_UITVOERING':
        return 'orange';
      case 'OPGESCHORT':
        return 'yellow';
      case 'VOLTOOID':
        return 'green';
      case 'GEANNULEERD':
        return 'red';
      default:
        return 'gray';
    }
  },

  getPrioriteitColor(prioriteit: WerkorderPrioriteit): string {
    switch (prioriteit) {
      case 'LAAG':
        return 'gray';
      case 'NORMAAL':
        return 'blue';
      case 'HOOG':
        return 'orange';
      case 'SPOED':
        return 'red';
      default:
        return 'gray';
    }
  },
};

export default werkordersService;
